// A session's transcript, live: `GET /v1/sessions/{id}/transcript?after=<item
// id>`, upgraded to a WebSocket (crates/poulpe-server/src/transcript.rs). The
// server sends the items after `after`, or all of them without it, then each
// new one: one JSON item per text message. The client sends nothing. It is
// signed in as the live feed is (api/feed.ts), and names the member the pages
// act as the same way, `&as=<member id>`; it closes with 4401 when the token
// or the person's session has ended. Once the session has ended, it sends the
// items left, then closes normally (1000), also when opened after the end.
import { transcriptPath } from './client'
import { FEED_ABNORMAL, FEED_BAD_MESSAGE, FEED_UNAUTHORIZED } from './feed'
import type { TranscriptItem } from './types'

/** The close code that says the token or the session has ended, as the feed's. */
export const TRANSCRIPT_UNAUTHORIZED = FEED_UNAUTHORIZED

/** Reported when the server sent what is not an item: the client closed the socket. */
export const TRANSCRIPT_BAD_MESSAGE = FEED_BAD_MESSAGE

/** Reported when the socket ended without a close frame: it failed, or never opened. */
export const TRANSCRIPT_ABNORMAL = FEED_ABNORMAL

/** The close code that says the session has ended and every item was sent: the transcript is whole. */
export const TRANSCRIPT_ENDED = 1000

export interface TranscriptHandlers {
  /** The socket is open: the items after the cursor follow. */
  open(): void
  item(item: TranscriptItem): void
  /** The socket closed, or could not open, with this code; never after `close()`. */
  closed(code: number): void
}

/** One connection to a transcript. */
export interface TranscriptSocket {
  /** Ends it: no handler is called after this. */
  close(): void
}

/** Opens a session's transcript after the item `after`, or from its start, as the member the pages act as (`as`), once they know who is signed in. */
export type OpenTranscript = (session: string, after: string | null, handlers: TranscriptHandlers, as?: string | null) => TranscriptSocket

/** The transcript's socket on the page's own origin: `ws:` beside `http:`, `wss:` beside `https:`; with `as`, when given. */
export function transcriptUrl(session: string, after: string | null, as: string | null = null, page: Pick<Location, 'protocol' | 'host'> = window.location): string {
  const path = transcriptPath(session, after)
  const named = as ? `${path.includes('?') ? '&' : '?'}as=${encodeURIComponent(as)}` : ''
  return `${page.protocol === 'https:' ? 'wss:' : 'ws:'}//${page.host}${path}${named}`
}

/** An item, from a page's entry; null when it is not one. Its payload is read later, as untrusted. */
export function itemOf(value: unknown): TranscriptItem | null {
  if (typeof value !== 'object' || value === null) return null
  const { id, at, source, payload } = value as Record<string, unknown>
  const ok = typeof id === 'string' && id !== '' && typeof at === 'string' && (source === 'runner' || source === 'sent' || source === 'poulpe') && typeof payload === 'object' && payload !== null
  return ok ? (value as TranscriptItem) : null
}

/** An item, from a message's text; null when it is not one. */
export function parseItem(data: unknown): TranscriptItem | null {
  if (typeof data !== 'string') return null
  try {
    return itemOf(JSON.parse(data))
  } catch {
    return null
  }
}

export interface WebSocketTranscriptOptions {
  /** The socket's URL for a session, a cursor and a member; the page's own origin by default. */
  url?: (session: string, after: string | null, as: string | null) => string
  /** The WebSocket class; the browser's by default. */
  WebSocket?: new (url: string) => WebSocket
}

/** Transcripts over the browser's WebSocket. */
export function webSocketTranscript({ url = transcriptUrl, WebSocket: Socket = globalThis.WebSocket }: WebSocketTranscriptOptions = {}): OpenTranscript {
  return (session, after, handlers, as = null) => {
    const ws = new Socket(url(session, after, as))
    let ended = false
    const end = (code: number) => {
      if (ended) return
      ended = true
      handlers.closed(code)
    }
    ws.onopen = () => {
      if (!ended) handlers.open()
    }
    ws.onmessage = (event: MessageEvent) => {
      if (ended) return
      const item = parseItem(event.data)
      if (item) {
        handlers.item(item)
        return
      }
      ws.close()
      end(TRANSCRIPT_BAD_MESSAGE)
    }
    // An error is followed by a close, which says how it ended.
    ws.onclose = (event: CloseEvent) => end(event.code || TRANSCRIPT_ABNORMAL)
    return {
      close() {
        if (ended) return
        ended = true
        ws.close()
      },
    }
  }
}
